import { useEffect, useState, useCallback, useMemo } from 'react'
import debounce from 'lodash.debounce'
import { getNewRiskScoresWithAssets } from 'api'
import {
    Container,
    DataGridTable,
    HeaderBar,
    Blade,
    TypoGraph,
    Divider,
    Card,
    MultiLineChart,
    Chip,
    Spinner,
    RangeColorCircle,
  } from 'components'
import { nbAssetActivityList } from 'constants/nbTableData'
import moment from 'moment'
import NbDetailsSummary from '../NbDetailsSummary'
import styles from './nbDetails.module.scss'
import colors from 'theme/colors'
import strings from 'strings'
import { forEach } from 'lodash'
import { useGridApiRef } from '@mui/x-data-grid'
import { isInsuranceLogin } from 'tools/auth'
import { exportTableAsCSV, formatDateMMDDYYYY } from 'tools/helper'
import { reportException } from 'tools/logs'
import AssetActivityScoringToolbar from './assetActivityScoringToolbar'
import {
  RiskScoresByAssetsResponse,
  RiskScoresByAssetsSummaryResponse,
  RiskScoreDashboardDetailResponse,
  RiskScoresByAssetsDetailWithFormattedDate,
  FleetPerformanceChartSeries,
} from './types/nbDetails'
import type { UnderwritersScoreRiskClassificationResponse } from 'api/types/dashboard'
import type { BlockRange } from 'components/rangeColorCircle'
import { GridRenderCellParams } from '@mui/x-data-grid'

const t = strings.CARRIERS

const RANGE_COLOR_CIRCLE_SIZE = 16

/** Build ranges and colors from API classification (worst → best: veryHigh, high, medium, low). */
function buildRangesFromClassification(
  classification: UnderwritersScoreRiskClassificationResponse | undefined
): { ranges: BlockRange[]; colors: string[] } | null {
  if (!classification) return null
  const bands: Array<{ lower: number; upper: number }> = []
  const order: (keyof UnderwritersScoreRiskClassificationResponse)[] = [
    'veryHighRisk',
    'highRisk',
    'mediumRisk',
    'lowRisk',
  ]
  for (const key of order) {
    const band = classification[key]
    if (band && band.lowerLimit != null && band.upperLimit != null) {
      bands.push({ lower: band.lowerLimit, upper: band.upperLimit })
    }
  }
  if (bands.length === 0) return null
  const ranges: BlockRange[] = bands.map((b) => [b.lower, b.upper])
  const colors =
    ranges.length === 3
      ? ['rgb(240, 66, 66)', 'rgb(231, 176, 8)', 'rgb(33, 196, 93)']
      : ranges.length === 4
        ? ['rgb(240, 66, 66)', 'rgb(249, 116, 21)', 'rgb(231, 176, 8)', 'rgb(33, 196, 93)']
        : bands.map(() => 'rgb(158, 158, 158)')
  return { ranges, colors }
}
const e = strings.CSV_EXPORT

const InsurerNbDetails: React.FC = () => {
    const [loading, setLoading] = useState(false)
    const [showAssetDetails, setShowAssetDetails] = useState(false)
    const [selectedRow] = useState<RiskScoresByAssetsDetailWithFormattedDate | undefined>()
    const [responseData, setResponseData] = useState<RiskScoresByAssetsResponse | undefined>()
    const [riskData, setRiskData] = useState<RiskScoreDashboardDetailResponse[]>([])
    const [riskAssetData, setRiskAssetData] = useState<RiskScoresByAssetsDetailWithFormattedDate[]>([])
    const [fleetPerformanceData, setFleetPerformanceData] = useState<FleetPerformanceChartSeries[]>([])
    const [summaryData, setSummaryData] = useState<RiskScoresByAssetsSummaryResponse | undefined>()
    const [activityFilter, setActivityFilter] = useState<string>('active')
    const [searchText, setSearchText] = useState<string>('')
    const { column: baseColumns, groups } = nbAssetActivityList
    const apiRef = useGridApiRef()

    const riskClassificationRanges = useMemo(
      () => buildRangesFromClassification(responseData?.underwritersScoreRiskClassification),
      [responseData?.underwritersScoreRiskClassification]
    )

    const INDEX_COLUMNS_WITH_CIRCLE = ['assetScore', 'safetyIndex', 'speedingIndex', 'geoIndex'] as const
    const getIndexValue = (
      field: (typeof INDEX_COLUMNS_WITH_CIRCLE)[number],
      row: RiskScoresByAssetsDetailWithFormattedDate
    ): number | undefined => {
      switch (field) {
        case 'assetScore':
          return row?.score != null ? Number(row.score) : undefined
        case 'safetyIndex':
          return row?.harsh?.index != null ? Number(row.harsh.index) : undefined
        case 'speedingIndex':
          return row?.speed?.index != null ? Number(row.speed.index) : undefined
        case 'geoIndex':
          return row?.geographical?.index != null ? Number(row.geographical.index) : undefined
        default:
          return undefined
      }
    }

    const column = useMemo(() => {
      if (!riskClassificationRanges) return baseColumns
      const { ranges, colors } = riskClassificationRanges
      return baseColumns.map((col: any) => {
        if (!INDEX_COLUMNS_WITH_CIRCLE.includes(col.field)) return col
        const field = col.field as (typeof INDEX_COLUMNS_WITH_CIRCLE)[number]
        const originalRenderCell = col.renderCell
        return {
          ...col,
          renderCell: (params: GridRenderCellParams) => {
            const value = getIndexValue(field, params.row as RiskScoresByAssetsDetailWithFormattedDate)
            if (value === undefined || value === null) {
              return originalRenderCell ? originalRenderCell(params) : (
                <TypoGraph content={strings.TABLE_DEFAULTS?.DASH_VALUE ?? '—'} />
              )
            }
            return (
              <span
                style={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  paddingTop: '4px',
                }}
              >
                <RangeColorCircle
                  value={value}
                  ranges={ranges}
                  colors={colors}
                  size={RANGE_COLOR_CIRCLE_SIZE}
                />
              </span>
            )
          },
        }
      })
    }, [baseColumns, riskClassificationRanges])

    const axisTitles = {
        yAxis: 'Index',
        xAxis: 'Score Date',
    }

    const chartColors = ['#7B1FA2', '#212121', '#795548', '#9E9E9E']

    useEffect(() => {
      setLoading(true)
      getNewRiskScoresWithAssets({ useKilometers: true })
        .then((res) => {
          setResponseData(res)
        })
        .catch((error) => {
          reportException(error)
        })
        .finally(() => {
          setLoading(false)
        })
    }, [])

    useEffect(() => {
      setSummaryData(responseData?.summary)
    }, [responseData])

    useEffect(() => {
      const filteredRiskData = responseData?.riskScores?.filter((item: RiskScoreDashboardDetailResponse) =>
        localStorage.getItem('eldProviderRisk')?.includes(item?.eldProvider ?? '')
      ) ?? []
      setRiskData(filteredRiskData)
    }, [summaryData])

    useEffect(() => {
      const graphData = getPerformanceData(riskData)
      setFleetPerformanceData(graphData)
    }, [riskData])

    useEffect(() => {
      const assetsData = responseData?.assetsDetails?.filter((item) =>
        localStorage.getItem('eldProviderRisk')?.includes(item?.eldProviderName ?? '')
      )
      if (assetsData && assetsData.length > 0) {
        const assetRiskScore = [...assetsData].sort(
          (a, b) => new Date(b.executionDate ?? 0).getTime() - new Date(a.executionDate ?? 0).getTime()
        )
        const uniqueAssets = new Set<string>()
        const filteredAssets: RiskScoresByAssetsDetailWithFormattedDate[] = assetRiskScore
          .filter((item) => {
            if (!uniqueAssets.has(item.assetEldId ?? '')) {
              uniqueAssets.add(item.assetEldId ?? '')
              return true
            }
            return false
          })
          .map((item) => ({
            ...item,
            executionDateFormatted: item.executionDate
              ? formatDateMMDDYYYY(item.executionDate)
              : null,
          }))
        setRiskAssetData(filteredAssets)
      }
    }, [fleetPerformanceData])

    const filteredData = useMemo(() => {
        if (!riskAssetData?.length) return []

        const now = new Date()
        const tenDaysAgo = new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000)

        const activityFilteredData = riskAssetData.filter((asset: RiskScoresByAssetsDetailWithFormattedDate) => {
            const executionDate = asset.executionDate ? new Date(asset.executionDate) : null

            switch (activityFilter) {
                case 'active':
                    return executionDate && executionDate >= tenDaysAgo
                case 'inactive':
                    return !executionDate || executionDate < tenDaysAgo
                default:
                    return true
            }
        })

        if (!searchText) {
            return activityFilteredData
        }

        const searchTerm = searchText.toLowerCase()
        return activityFilteredData.filter((item: RiskScoresByAssetsDetailWithFormattedDate) => {
            const values = Object.values(item)
            for (let value of values) {
                if (typeof value === 'string' && value.toLowerCase().includes(searchTerm)) {
                    return true
                }
            }
            return false
        })
    }, [riskAssetData, activityFilter, searchText])

    const debouncedSearchChange = useMemo(
        () => debounce((searchValue: string) => {
            setSearchText(searchValue)
        }, 1000),
        []
    )

    // Cleanup debounced function on unmount
    useEffect(() => {
        return () => {
            debouncedSearchChange.cancel()
        }
    }, [debouncedSearchChange])

    const loadPagination = (pageNumber: number, pageSize: number) => {
        console.log(`pagenumber: ${pageNumber}, pageSize: ${pageSize}`)
    }

    function formatDate(inputDateString: string) {
        const inputDate = new Date(inputDateString);
        const month = String(inputDate.getMonth() + 1).padStart(2, '0');
        const day = String(inputDate.getDate()).padStart(2, '0');

        return `${month}-${day}`;
    }

    const getPerformanceData = (data: RiskScoreDashboardDetailResponse[]): FleetPerformanceChartSeries[] => {
        const summaryArray: Array<{ x: string; y: number }> = []
        const harshIndexArray: Array<{ x: string; y: number }> = []
        const geoIndexArray: Array<{ x: string; y: number }> = []
        const speedIndexArray: Array<{ x: string; y: number }> = []
        forEach([...(data ?? [])].reverse(), (item: RiskScoreDashboardDetailResponse) => {
            const rowDate = formatDate(item?.periodEndDate ?? '')
            summaryArray.push({
                x: rowDate,
                y: item?.fleetScore ?? 0
            })
            harshIndexArray.push({
                x: rowDate,
                y: item?.harsh?.index ?? 0
            })
            geoIndexArray.push({
                x: rowDate,
                y: item?.geographical?.index ?? 0
            })
            speedIndexArray.push({
                x: rowDate,
                y: item?.speed?.index ?? 0
            })
        })

        const summaryData = {
            'id': 'Summary',
            'label': 'Summary',
            'color': '#7B1FA2',
            'data': summaryArray
        }
        const harshIndexData = {
            'id': 'Harsh Index',
            'label': 'Harsh Index',
            'color': '#212121',
            'data': harshIndexArray
        }
        const speedIndexData = {
            'id': 'Speeding Index',
            'label': 'Speeding Index',
            'color': '#9E9E9E',
            'data': speedIndexArray
        }
        const geoIndexData = {
            'id': 'Geo Index',
            'label': 'Geo Index',
            'color': '#795548',
            'data': geoIndexArray
        }

        const fleetData: FleetPerformanceChartSeries[] = []
        fleetData.push(summaryData)
        fleetData.push(harshIndexData)
        fleetData.push(geoIndexData)
        fleetData.push(speedIndexData)

        return fleetData
    }

    const handleRiskExport = () => {
        const csvString = apiRef.current.getDataAsCsv()
        const csvRows = csvString.split('\n')
        const modifiedCsv = csvRows.slice(1).join('\n')
        exportTableAsCSV(modifiedCsv, e.ASSETS_CSV_EXPORT_NAME)
      }

      const tableMenuItems = [
        {
          label: e.DOWNLOAD_AS_CSV,
          key: 'export',
          action: () => {
            handleRiskExport()
          },
        },
      ]

    const ToolbarComponent = useCallback(
        (props: any) => (
            <AssetActivityScoringToolbar
                activityFilter={activityFilter}
                setActivityFilter={setActivityFilter}
                onSearchChange={debouncedSearchChange}
                menuItems={tableMenuItems}
                searchValue={searchText}
                loading={loading}
            />
        ),
        [activityFilter, setActivityFilter, debouncedSearchChange, loading]
    )

    const BladeContent = () => {
        return (
          <Container className={styles.bladeBodyContainer}>
            <Container className={styles.bladeTabContainer}>

            </Container>
            <Container className={styles.documentsSection}>
              <TypoGraph variant="h6" content="Documents" />
              <Divider />

            </Container>
          </Container>
        )
    }

    const HeaderDom = () => {
        return (
            <Container className={styles.headerContainer} data-testid="nb-details-header">
                <Container className={styles.headerTitleContainer}>
                    <TypoGraph
                        variant="h1"
                        sx={{ marginRight: '1rem' }}
                        mb={0}
                        content={(!isInsuranceLogin ? localStorage.getItem('carrierName') : 'Carrier Details') ?? 'Carrier Details'}
                        dataTestId="nb-details-carrier-name"
                    />
                    <Chip
                        label={`Updated ${moment().format('dddd, MMMM Do')}`}
                        variant="filled"
                        sx={{
                            backgroundColor: '#E1FAEB',
                            color: '#20693D',
                        }}
                        dataTestId="nb-details-updated-chip"
                    />
                </Container>
            </Container>
        )
    }

    return (
        <Container className={styles.insuranceBladeBodyContainer} data-testid="nb-details-page">
            <Container data-testid="nb-details-content">
                <HeaderBar dom={<HeaderDom />} />
                <TypoGraph
                    variant="h2"
                    sx={{ marginRight: '1rem', marginTop: '-15px', marginBottom: '1rem' }}
                    mb={0}
                    content={localStorage.getItem('eldProviderRisk') ?? ''}
                    dataTestId="nb-details-eld-provider-title"
                />
                {/* Summary Header */}
                <NbDetailsSummary
                    summaryData={summaryData}
                    assetCount={riskAssetData?.length}
                    loading={loading}
                    testIdPrefix="nb-details"
                    styles={styles}
                />
                {/* Fleet Graph */}
                <Container className={styles.chartContainer} sx={{marginBottom: '1rem'}} data-testid="nb-details-fleet-performance-chart">
                    <Card containerClass="flexAlignCenter overflowVisible">
                    {(loading && <Spinner />) || (
                        <>
                            <TypoGraph content="Fleet Performance" />
                            <MultiLineChart data={fleetPerformanceData} axisTitles={axisTitles} colors={chartColors} />
                        </>
                    )}
                    </Card>
                </Container>
                {/* Asset Activity & Scoring */}
                <div data-testid="nb-details-asset-activity-table">
                <DataGridTable
                    apiRef={apiRef}
                    title={t.DETAILS_TAB.ASSET_ACTIVITY_SCORING}
                    caption={t.DETAILS_TAB.TABLE_CAPTION}
                    showCaption={true}
                    loading={loading}
                    data={filteredData || []}
                    columns={column}
                    columnGroupingModel={groups}
                    columnHeaderHeight={30}
                    columnVisibilityModel={{assetId: false}}
                    totalCount={filteredData?.length}
                    reduceHeight={260}
                    checkboxSelection={false}
                    multiSearchFilter={false}
                    onSingleQuickFilterChange={debouncedSearchChange}
                    getRowId={(row: RiskScoresByAssetsDetailWithFormattedDate) => row?.assetId ?? ''}
                    handlePagination={(pageNumber, pageSize) =>
                        loadPagination(pageNumber, pageSize)
                    }
                    menuList={tableMenuItems}
                    showFilter={false}
                    components={{ toolbar: ToolbarComponent }}
                />
                </div>
                <Blade
                    title={selectedRow?.assetEldId}
                    open={showAssetDetails}
                    onClose={() => setShowAssetDetails(false)}
                    bodyNode={<BladeContent />}
                    data-testid="nb-details-asset-blade"
                />
            </Container>
        </Container>
    )
}

export default InsurerNbDetails
